import React from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X, Paintbrush, ChevronUp, ChevronDown } from "lucide-react";
import { asColor, ruleEffects } from "@/lib/condFormat";

// condition presets: single select mapping to {type, op}
const CONDS = [
  { v: "value:gt", label: "> greater than" },
  { v: "value:gte", label: "≥ at least" },
  { v: "value:lt", label: "< less than" },
  { v: "value:lte", label: "≤ at most" },
  { v: "value:eq", label: "= equal to" },
  { v: "value:neq", label: "≠ not equal to" },
  { v: "value:between", label: "between" },
  { v: "value:not_between", label: "not between" },
  { v: "value:contains", label: "contains" },
  { v: "value:starts_with", label: "starts with" },
  { v: "value:ends_with", label: "ends with" },
  { v: "blank:", label: "is blank" },
  { v: "not_blank:", label: "is not blank" },
  { v: "top_n:", label: "Top N" },
  { v: "bottom_n:", label: "Bottom N" },
  { v: "above_avg:", label: "above average" },
  { v: "below_avg:", label: "below average" },
  { v: "formula:", label: "custom formula" },
];

const ICONS = [
  { v: "arrow_up", label: "↑ Arrow up" },
  { v: "arrow_down", label: "↓ Arrow down" },
  { v: "circle", label: "● Circle" },
  { v: "triangle", label: "▲ Warning" },
  { v: "flag", label: "⚑ Flag" },
  { v: "check", label: "✓ Check" },
  { v: "x", label: "✕ Cross" },
  { v: "minus", label: "− Dash" },
];

// One composable formatting layer row: enable checkbox + inline controls.
function LayerRow({ label, on, onToggle, testid, children }) {
  return (
    <div className="flex items-center gap-1.5 min-h-[24px]">
      <label className="flex items-center gap-1.5 w-[86px] shrink-0 cursor-pointer">
        <input type="checkbox" data-testid={testid} checked={!!on} onChange={(e) => onToggle(e.target.checked)}
          className="accent-[color:var(--brand-ocean)] cursor-pointer" />
        <span className="text-[11px] text-[color:var(--ink-2)]">{label}</span>
      </label>
      {on && <div className="flex items-center gap-1 flex-1 min-w-0 flex-wrap">{children}</div>}
    </div>
  );
}

function FontToggle({ on, onClick, testid, children, title }) {
  return (
    <button data-testid={testid} title={title} onClick={onClick}
      className={"w-6 h-6 rounded border text-[11px] leading-none " + (on
        ? "bg-[color:var(--brand-ocean)] text-white border-[color:var(--brand-ocean)]"
        : "bg-[color:var(--surface-2)] text-[color:var(--ink-3)] border-[color:var(--glass-border)] hover:text-[color:var(--ink)]")}>
      {children}
    </button>
  );
}

const dimOut = (d) => d.key || d.alias || (d.date_part && d.date_part !== "none" ? `${d.field}_${d.date_part}` : d.field);
const measOut = (m) => m.key || m.alias || `${m.agg || "sum"}_${m.field}`;

function ColorPick({ value, onChange, testid, title }) {
  return <input data-testid={testid} type="color" title={title} value={asColor(value, "#E85D6A")} onChange={(e) => onChange(e.target.value)}
    className="h-7 w-8 rounded-md border border-[color:var(--glass-border)] bg-transparent cursor-pointer shrink-0" />;
}

// Items 62-63: full conditional formatting rule builder (priority = list order)
export function CondFormatEditor({ config, opts, patchOpts, widgetType }) {
  const rules = opts.condRules || [];
  const setRules = (arr) => patchOpts({ condRules: arr });
  const isKpi = widgetType === "kpi";
  const measures = (config.measures || []).map(measOut).filter(Boolean);
  const dims = (config.dimensions || []).map(dimOut).filter(Boolean);
  const targets = [...measures, ...dims];
  if (isKpi && !targets.includes("value")) targets.push("value");

  const add = () => setRules([...rules, {
    scope: "cell", target: measures[0] || targets[0] || "value", type: "value", op: "gt", value: "",
    effects: { bg: { on: true, color: "#FDE2E4" }, text: { on: true, color: "#B4232F" } }, enabled: true,
  }]);
  const upd = (i, patch) => setRules(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const rm = (i) => setRules(rules.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rules.length) return;
    const arr = [...rules]; [arr[i], arr[j]] = [arr[j], arr[i]];
    setRules(arr);
  };

  return (
    <div data-testid="condformat-editor" className="space-y-2 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><Paintbrush className="w-3.5 h-3.5" />Conditional formatting ({rules.length})</Label>
        <Button data-testid="cf-add-rule" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]" onClick={add}><Plus className="w-3.5 h-3.5" /></Button>
      </div>
      {rules.length > 1 && <p className="text-[10px] text-[color:var(--ink-3)]">Rules run top-down and their layers combine — the first rule to set a layer (text, fill, icon…) wins it. Reorder with the arrows.</p>}
      {rules.map((r, i) => {
        const condV = `${r.type || "value"}:${(r.type || "value") === "value" ? (r.op || "gt") : ""}`;
        const setCond = (v) => {
          const [type, op] = v.split(":");
          upd(i, { type, op: op || undefined });
        };
        const ef = ruleEffects(r);
        const setEf = (layer, patch) => upd(i, { effects: { ...ef, [layer]: { ...(ef[layer] || {}), ...patch } }, effect: undefined });
        const toggleEf = (layer, on, defaults = {}) => setEf(layer, { on, ...(on ? defaults : {}) });
        return (
          <div key={i} data-testid={`cf-rule-${i}`} className={"rounded-lg bg-[color:var(--surface)] border border-[color:var(--glass-border)] p-2 space-y-1.5 " + (r.enabled === false ? "opacity-55" : "")}>
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-semibold text-[color:var(--ink-3)] w-4">{i + 1}.</span>
              <button data-testid={`cf-up-${i}`} disabled={i === 0} onClick={() => move(i, -1)} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30 p-0.5"><ChevronUp className="w-3.5 h-3.5" /></button>
              <button data-testid={`cf-down-${i}`} disabled={i === rules.length - 1} onClick={() => move(i, 1)} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30 p-0.5"><ChevronDown className="w-3.5 h-3.5" /></button>
              {!isKpi && (
                <Select value={r.scope || "cell"} onValueChange={(v) => upd(i, { scope: v })}>
                  <SelectTrigger data-testid={`cf-scope-${i}`} className="bg-[color:var(--surface-2)] h-7 w-[88px] text-[11px] shrink-0"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cell">Cell</SelectItem>
                    <SelectItem value="row">Entire row</SelectItem>
                    <SelectItem value="widget">Widget</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <div className="ml-auto flex items-center gap-1">
                <Switch data-testid={`cf-enable-${i}`} checked={r.enabled !== false} onCheckedChange={(v) => upd(i, { enabled: v })} className="scale-75" />
                <button data-testid={`cf-delete-${i}`} onClick={() => rm(i)} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] p-0.5"><X className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <div className="flex gap-1.5">
              <Select value={r.target || targets[0] || "value"} onValueChange={(v) => upd(i, { target: v })}>
                <SelectTrigger data-testid={`cf-target-${i}`} className="bg-[color:var(--surface-2)] flex-1 min-w-0 h-7 text-[11px]"><SelectValue placeholder="Column" /></SelectTrigger>
                <SelectContent>{targets.map((t) => <SelectItem key={t} value={t}>{t === "value" ? "KPI value" : t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={condV} onValueChange={setCond}>
                <SelectTrigger data-testid={`cf-cond-${i}`} className="bg-[color:var(--surface-2)] flex-1 min-w-0 h-7 text-[11px]"><SelectValue /></SelectTrigger>
                <SelectContent>{CONDS.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(r.type || "value") === "value" && (
              <div className="flex gap-1.5">
                <Input data-testid={`cf-value-${i}`} value={r.value ?? ""} onChange={(e) => upd(i, { value: e.target.value })} placeholder="Value" className="bg-[color:var(--surface-2)] h-7 text-xs flex-1 min-w-0" />
                {["between", "not_between"].includes(r.op) && (
                  <Input data-testid={`cf-value2-${i}`} value={r.value2 ?? ""} onChange={(e) => upd(i, { value2: e.target.value })} placeholder="and" className="bg-[color:var(--surface-2)] h-7 text-xs flex-1 min-w-0" />
                )}
              </div>
            )}
            {["top_n", "bottom_n"].includes(r.type) && (
              <Input data-testid={`cf-n-${i}`} type="number" min={1} value={r.n ?? 10} onChange={(e) => upd(i, { n: Number(e.target.value) || 1 })} placeholder="N" className="bg-[color:var(--surface-2)] h-7 text-xs w-24" />
            )}
            {r.type === "formula" && (
              <div>
                <Input data-testid={`cf-formula-${i}`} value={r.formula ?? ""} onChange={(e) => upd(i, { formula: e.target.value })} placeholder={`e.g. ${targets[0] || "sum_amount"} > 100 && ${targets[1] || "region"} == 'North'`} className="bg-[color:var(--surface-2)] h-7 text-xs font-mono" />
                <p className="text-[10px] text-[color:var(--ink-3)] mt-0.5">Use column names, comparison and &&/|| operators.</p>
              </div>
            )}
            <div className="rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface-2)]/50 p-1.5 space-y-0.5" data-testid={`cf-layers-${i}`}>
              <LayerRow label="Text color" on={ef.text?.on} onToggle={(v) => toggleEf("text", v, { color: ef.text?.color || "#B4232F" })} testid={`cf-layer-text-${i}`}>
                <ColorPick testid={`cf-text-color-${i}`} title="Text color" value={ef.text?.color || "#B4232F"} onChange={(v) => setEf("text", { color: v })} />
              </LayerRow>
              <LayerRow label="Fill" on={ef.bg?.on} onToggle={(v) => toggleEf("bg", v, { color: ef.bg?.color || "#FDE2E4" })} testid={`cf-layer-bg-${i}`}>
                <ColorPick testid={`cf-bg-color-${i}`} title="Cell fill" value={ef.bg?.color || "#FDE2E4"} onChange={(v) => setEf("bg", { color: v })} />
              </LayerRow>
              <LayerRow label="Font style" on={ef.font?.on} onToggle={(v) => toggleEf("font", v, { bold: ef.font?.bold ?? true })} testid={`cf-layer-font-${i}`}>
                <FontToggle testid={`cf-font-bold-${i}`} title="Bold" on={ef.font?.bold} onClick={() => setEf("font", { bold: !ef.font?.bold })}><b>B</b></FontToggle>
                <FontToggle testid={`cf-font-italic-${i}`} title="Italic" on={ef.font?.italic} onClick={() => setEf("font", { italic: !ef.font?.italic })}><i>I</i></FontToggle>
                <FontToggle testid={`cf-font-underline-${i}`} title="Underline" on={ef.font?.underline} onClick={() => setEf("font", { underline: !ef.font?.underline })}><u>U</u></FontToggle>
                <FontToggle testid={`cf-font-strike-${i}`} title="Strikethrough" on={ef.font?.strike} onClick={() => setEf("font", { strike: !ef.font?.strike })}><s>S</s></FontToggle>
              </LayerRow>
              <LayerRow label="Icon" on={ef.icon?.on} onToggle={(v) => toggleEf("icon", v, { icon: ef.icon?.icon || "circle", color: ef.icon?.color || "#E85D6A" })} testid={`cf-layer-icon-${i}`}>
                <Select value={ef.icon?.icon || "circle"} onValueChange={(v) => setEf("icon", { icon: v })}>
                  <SelectTrigger data-testid={`cf-icon-${i}`} className="bg-[color:var(--surface-2)] w-[96px] h-6 text-[10px] shrink-0"><SelectValue /></SelectTrigger>
                  <SelectContent>{ICONS.map((ic) => <SelectItem key={ic.v} value={ic.v}>{ic.label}</SelectItem>)}</SelectContent>
                </Select>
                <ColorPick testid={`cf-icon-color-${i}`} title="Icon color" value={ef.icon?.color || "#E85D6A"} onChange={(v) => setEf("icon", { color: v })} />
                <Select value={ef.icon?.position || "left"} onValueChange={(v) => setEf("icon", { position: v })}>
                  <SelectTrigger data-testid={`cf-icon-pos-${i}`} className="bg-[color:var(--surface-2)] w-[86px] h-6 text-[10px] shrink-0"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left">Left of text</SelectItem>
                    <SelectItem value="right">Right of text</SelectItem>
                    <SelectItem value="only">Icon only</SelectItem>
                  </SelectContent>
                </Select>
              </LayerRow>
              {(r.scope || "cell") === "cell" && (
                <>
                  <LayerRow label="Data bar" on={ef.databar?.on} onToggle={(v) => toggleEf("databar", v, { color: ef.databar?.color || "#2B4EFF" })} testid={`cf-layer-databar-${i}`}>
                    <ColorPick testid={`cf-databar-color-${i}`} title="Bar color" value={ef.databar?.color || "#2B4EFF"} onChange={(v) => setEf("databar", { color: v })} />
                    <span className="text-[9px] text-[color:var(--ink-3)]">whole column</span>
                  </LayerRow>
                  <LayerRow label="Color scale" on={ef.scale?.on} onToggle={(v) => toggleEf("scale", v, { min: ef.scale?.min || "#FDE68A", max: ef.scale?.max || "#00C2A8" })} testid={`cf-layer-scale-${i}`}>
                    <ColorPick testid={`cf-scalemin-${i}`} title="Low color" value={ef.scale?.min || "#FDE68A"} onChange={(v) => setEf("scale", { min: v })} />
                    <ColorPick testid={`cf-scalemax-${i}`} title="High color" value={ef.scale?.max || "#00C2A8"} onChange={(v) => setEf("scale", { max: v })} />
                    <span className="text-[9px] text-[color:var(--ink-3)]">whole column</span>
                  </LayerRow>
                </>
              )}
              <div className="flex items-center justify-end gap-1.5 pt-0.5">
                <span className="text-[10px] text-[color:var(--ink-3)]">Stop if true</span>
                <Switch data-testid={`cf-stop-${i}`} checked={!!r.stopIfTrue} onCheckedChange={(v) => upd(i, { stopIfTrue: v })} className="scale-75" />
              </div>
            </div>
          </div>
        );
      })}
      {rules.length === 0 && <p className="text-xs text-[color:var(--ink-3)]">No rules yet — highlight cells, rows or the whole widget when conditions are met.</p>}
    </div>
  );
}
